/**
 * RoundIssuesStore.ts
 * Zustand store for Round Issues management
 * Feature 4.4 - Issue Identification & Work Order Integration
 */
import { create } from "zustand";
import axiosInstance from "../common/axiosInstance";
import { handleApiError } from "../common/utils";

// Severity levels
export const SEVERITY_LEVELS = ["Critical", "High", "Medium", "Low"] as const;
export type SeverityLevel = (typeof SEVERITY_LEVELS)[number];

// Issue status
export const ISSUE_STATUSES = [
  "Open",
  "In Progress",
  "Resolved",
  "Closed",
] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

// Assign to options
export const ASSIGN_TO_OPTIONS = ["Maintenance", "Safety"] as const;
export type AssignTo = (typeof ASSIGN_TO_OPTIONS)[number];

// Location interface
export interface IssueLocation {
  region?: string;
  premises?: string;
  premisesId?: string;
  area?: string;
  department?: string;
}

// Round Issue interface
export interface RoundIssue {
  _id?: string;
  id?: string;
  issueNumber: string;
  title: string;
  description: string;
  severity: SeverityLevel;
  location: IssueLocation;
  linkedChecklistItemId?: string;
  linkedRoundPlanId?: string;
  linkedRoundExecutionId?: string;
  photoEvidence: string[];
  assignTo: AssignTo;
  assignedToUserId?: string;
  assignedToUserName?: string;
  status: IssueStatus;
  workOrderId?: string;
  incidentId?: string;
  resolutionNotes?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  tenantId?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Dashboard stats interface
export interface IssueDashboardStats {
  summary: {
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
    closed: number;
  };
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  recentIssues: RoundIssue[];
}

// Pagination interface
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Create issue input
export interface CreateIssueInput {
  title: string;
  description: string;
  severity: SeverityLevel;
  location?: IssueLocation;
  linkedChecklistItemId?: string;
  linkedRoundPlanId?: string;
  linkedRoundExecutionId?: string;
  photoEvidence?: string[];
  assignTo: AssignTo;
  assignedToUserId?: string;
  assignedToUserName?: string;
}

// Update issue input
export interface UpdateIssueInput {
  title?: string;
  description?: string;
  severity?: SeverityLevel;
  status?: IssueStatus;
  assignTo?: AssignTo;
  assignedToUserId?: string;
  assignedToUserName?: string;
  resolutionNotes?: string;
  photoEvidence?: string[];
}

// Filter options
export interface IssueFilters {
  severity?: SeverityLevel;
  status?: IssueStatus;
  assignTo?: AssignTo;
  linkedRoundPlanId?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// Store state interface
interface RoundIssuesState {
  // State
  issues: RoundIssue[];
  currentIssue: RoundIssue | null;
  dashboardStats: IssueDashboardStats | null;
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;

  // Actions
  fetchIssues: (filters?: IssueFilters) => Promise<void>;
  fetchIssueById: (id: string) => Promise<void>;
  createIssue: (data: CreateIssueInput) => Promise<RoundIssue>;
  updateIssue: (id: string, data: UpdateIssueInput) => Promise<RoundIssue>;
  deleteIssue: (id: string) => Promise<boolean>;
  linkIssueToIncident: (
    issueId: string,
    incidentId: string
  ) => Promise<RoundIssue>;
  fetchDashboardStats: (roundPlanId?: string) => Promise<void>;
  setCurrentIssue: (issue: RoundIssue | null) => void;
  clearError: () => void;
}

export const useRoundIssuesStore = create<RoundIssuesState>((set, get) => ({
  // Initial state
  issues: [],
  currentIssue: null,
  dashboardStats: null,
  pagination: null,
  loading: false,
  error: null,

  // Fetch all issues with filters
  fetchIssues: async (filters = {}) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();

      if (filters.severity) params.append("severity", filters.severity);
      if (filters.status) params.append("status", filters.status);
      if (filters.assignTo) params.append("assignTo", filters.assignTo);
      if (filters.linkedRoundPlanId)
        params.append("linkedRoundPlanId", filters.linkedRoundPlanId);
      if (filters.search) params.append("search", filters.search);
      if (filters.page) params.append("page", filters.page.toString());
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

      const response = await axiosInstance.get(
        `/api/round-issues${
          params.toString() ? `?${params.toString()}` : ""
        }`
      );

      if (response.data.success) {
        set({
          issues: response.data.data || [],
          pagination: response.data.pagination || null,
          loading: false,
          error: null,
        });
      } else {
        throw new Error(response.data.error || "Failed to fetch issues");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch issues";
      handleApiError(error, errorMessage);
      set({ loading: false, error: errorMessage });
    }
  },

  // Fetch single issue by ID
  fetchIssueById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosInstance.get(`/api/round-issues/${id}`);

      if (response.data.success) {
        set({
          currentIssue: response.data.data,
          loading: false,
          error: null,
        });
      } else {
        throw new Error(response.data.error || "Failed to fetch issue");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || "Failed to fetch issue";
      handleApiError(error, errorMessage);
      set({ loading: false, error: errorMessage });
    }
  },

  // Create new issue
  createIssue: async (data: CreateIssueInput) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosInstance.post(
        `/api/round-issues`,
        data
      );

      if (response.data.success) {
        const newIssue = response.data.data;
        set((state) => ({
          issues: [newIssue, ...state.issues],
          currentIssue: newIssue,
          loading: false,
          error: null,
        }));
        return newIssue;
      } else {
        throw new Error(response.data.error || "Failed to create issue");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to create issue";
      handleApiError(error, errorMessage);
      set({ loading: false, error: errorMessage });
      throw error;
    }
  },

  // Update issue
  updateIssue: async (id: string, data: UpdateIssueInput) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosInstance.put(
        `/api/round-issues/${id}`,
        data
      );

      if (response.data.success) {
        const updatedIssue = response.data.data;
        set((state) => ({
          issues: state.issues.map((issue) =>
            issue._id === id || issue.id === id ? updatedIssue : issue
          ),
          currentIssue:
            state.currentIssue?._id === id || state.currentIssue?.id === id
              ? updatedIssue
              : state.currentIssue,
          loading: false,
          error: null,
        }));
        return updatedIssue;
      } else {
        throw new Error(response.data.error || "Failed to update issue");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to update issue";
      handleApiError(error, errorMessage);
      set({ loading: false, error: errorMessage });
      throw error;
    }
  },

  // Delete issue
  deleteIssue: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosInstance.delete(
        `/api/round-issues/${id}`
      );

      if (response.data.success) {
        set((state) => ({
          issues: state.issues.filter(
            (issue) => issue._id !== id && issue.id !== id
          ),
          currentIssue:
            state.currentIssue?._id === id || state.currentIssue?.id === id
              ? null
              : state.currentIssue,
          loading: false,
          error: null,
        }));
        return true;
      } else {
        throw new Error(response.data.error || "Failed to delete issue");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to delete issue";
      handleApiError(error, errorMessage);
      set({ loading: false, error: errorMessage });
      return false;
    }
  },

  // Link issue to incident
  linkIssueToIncident: async (issueId: string, incidentId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosInstance.post(
        `/api/round-issues/${issueId}/link-incident`,
        { incidentId }
      );

      if (response.data.success) {
        const updatedIssue = response.data.data;
        set((state) => ({
          issues: state.issues.map((issue) =>
            issue._id === issueId || issue.id === issueId ? updatedIssue : issue
          ),
          currentIssue:
            state.currentIssue?._id === issueId ||
            state.currentIssue?.id === issueId
              ? updatedIssue
              : state.currentIssue,
          loading: false,
          error: null,
        }));
        return updatedIssue;
      } else {
        throw new Error(
          response.data.error || "Failed to link issue to incident"
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to link issue to incident";
      handleApiError(error, errorMessage);
      set({ loading: false, error: errorMessage });
      throw error;
    }
  },

  // Fetch dashboard statistics
  fetchDashboardStats: async (roundPlanId?: string) => {
    set({ loading: true, error: null });
    try {
      const params = roundPlanId ? `?linkedRoundPlanId=${roundPlanId}` : "";
      const response = await axiosInstance.get(
        `/api/round-issues/dashboard/stats${params}`
      );

      if (response.data.success) {
        set({
          dashboardStats: response.data.data,
          loading: false,
          error: null,
        });
      } else {
        throw new Error(
          response.data.error || "Failed to fetch dashboard stats"
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch dashboard stats";
      handleApiError(error, errorMessage);
      set({ loading: false, error: errorMessage });
    }
  },

  // Set current issue
  setCurrentIssue: (issue) => {
    set({ currentIssue: issue });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));
